/**
 * frontend/src/features/telephony/services/telephonyApi.js
 * ---------------------------------------------------------
 * Centralized API service for Bridge Telephony (MULTI-APP model).
 *
 * Credential tiers (map to backend tables):
 *   Account (tenant-level, one row)   — account_sid, exotel_api_key, exotel_api_token,
 *                                        exotel_domain, customer_id, customer_secret,
 *                                        customer_token(+expiry)
 *   App (Tier 3, many per tenant)     — app_id, app_secret, app_name, exophone_number,
 *                                        app_token(+expiry)
 *   Agent (scoped to an app)          — user_id, app_user_id, exotel_user_sid, sip uri
 *
 * Endpoint contract:
 *   GET    /telephony/account
 *   POST   /telephony/account
 *   POST   /telephony/account/validate-customer
 *   GET    /telephony/apps
 *   POST   /telephony/apps
 *   POST   /telephony/apps/{id}/refresh
 *   DELETE /telephony/apps/{id}
 *   GET    /telephony/apps/{id}/settings
 *   POST   /telephony/apps/{id}/settings/webhooks
 *   POST   /telephony/apps/{id}/settings          (add a single key-value setting)
 *   DELETE /telephony/apps/{id}/settings?key=     (delete a single key)
 *   GET    /telephony/apps/{id}/agents
 *   POST   /telephony/apps/{id}/agents
 *   DELETE /telephony/apps/{id}/agents/{userId}
 *   PUT    /telephony/apps/{id}/agents/{userId}/device (update device status)
 *   GET    /telephony/agent-session          (unchanged — softphone)
 *   POST   /telephony/agent-device           (unchanged — softphone)
 *   GET    /telephony/call-logs              (unchanged)
 *   GET    /telephony/contacts               (unchanged — dialer)
 */

import api from '../../../api/client'

// ── Account (tenant-level credentials) ───────────────────────────────────

/** Fetch tenant Exotel account + customer credentials. Secrets returned masked. */
export async function fetchAccount() {
  const { data } = await api.get('/telephony/account')
  return data // { success, data: { account_sid, exotel_domain, exophone_number, customer_token_expires_at, ... } }
}

/**
 * Save / update the tenant-level Exotel account + customer credentials.
 * Masked placeholder values are preserved server-side.
 * exophone_number is the single shared virtual number every agent mapping inherits.
 * @param {object} payload {
 *   account_sid, exotel_api_key, exotel_api_token,
 *   exotel_domain, customer_id, customer_secret, exophone_number
 * }
 */
export async function saveAccount(payload) {
  const { data } = await api.post('/telephony/account', payload)
  return data // { success, message, data: { id } }
}

/**
 * Mint (and cache) the customer token to verify the Customer ID / Secret pair.
 * @returns {{ success, data: { expires_at } }}
 */
export async function validateCustomerToken() {
  const { data } = await api.post('/telephony/account/validate-customer', {})
  return data
}

// ── Apps (many per tenant) ───────────────────────────────────────────────

/** List all Exotel apps registered under this tenant. */
export async function fetchApps() {
  const { data } = await api.get('/telephony/apps')
  return data // { success, data: [{ id, app_id, app_name, exophone_number, is_active, app_token_expires_at, agent_count }] }
}

/**
 * Create a new Exotel app (customer-token → POST /v2/integrations/app).
 * Persists the returned AppID + AppSecret as a new TelephonyApps row.
 * Note: ExoPhone is NOT part of app creation — it's a per-mapping value sourced
 * from the account when an agent is provisioned.
 * @param {{ app_name: string }} payload
 */
export async function createApp(payload) {
  const { data } = await api.post('/telephony/apps', payload)
  return data // { success, message, data: { id, app_id, app_name, ... } }
}

/** Re-fetch an app from Exotel and re-mint its app token. */
export async function refreshApp(appRowId) {
  const { data } = await api.post(`/telephony/apps/${appRowId}/refresh`, {})
  return data
}

/** Delete an app registration (also removes its agent mappings server-side). */
export async function deleteApp(appRowId) {
  const { data } = await api.delete(`/telephony/apps/${appRowId}`)
  return data
}

// ── App settings (webhooks, per app) ─────────────────────────────────────

/** Fetch the webhook / app_setting URLs currently registered for this app. */
export async function fetchAppSettings(appRowId) {
  const { data } = await api.get(`/telephony/apps/${appRowId}/settings`)
  return data // { success, data: [{ Key, Value, ... }] }
}

/**
 * Register popup + call-completion webhook URLs for this app.
 * @param {number} appRowId
 * @param {{ base_url: string }} payload
 */
export async function registerAppWebhooks(appRowId, payload) {
  const { data } = await api.post(`/telephony/apps/${appRowId}/settings/webhooks`, payload)
  return data
}

/**
 * Add (or update) a single Exotel app_setting key-value pair for this app.
 * Exotel's API: POST /v2/integrations/app_setting  {Key, Value}
 * Each call sets exactly one setting key.
 * @param {number} appRowId
 * @param {{ key: string, value: string }} payload  e.g. { key: 'popup', value: 'https://...' }
 */
export async function addAppSetting(appRowId, payload) {
  const { data } = await api.post(`/telephony/apps/${appRowId}/settings`, payload)
  return data // { success, message, data: [...] }
}

/**
 * Delete a single Exotel app_setting by key for this app.
 * Exotel's API: DELETE /v2/integrations/app_setting?key=<key>
 * @param {number} appRowId
 * @param {string} key  e.g. 'popup' | 'callback' | 'incomingcallhungup'
 */
export async function deleteAppSetting(appRowId, key) {
  const { data } = await api.delete(`/telephony/apps/${appRowId}/settings`, {
    params: { key },
  })
  return data
}

// ── Agents (scoped to an app) ────────────────────────────────────────────

/**
 * List tenant employees left-joined with THIS app's agent mappings.
 * Provisioned agents carry exotel_user_sid + device_contact_uri.
 */
export async function fetchAppAgents(appRowId) {
  const { data } = await api.get(`/telephony/apps/${appRowId}/agents`)
  return data // { success, data: [{ user_id, employee_name, email, phone_number, is_provisioned, ... }] }
}

/**
 * Provision a Bridge employee as an Exotel agent UNDER this app.
 * The backend sources VirtualNumber (ExoPhone) from the account row.
 * @param {number} appRowId
 * @param {{ user_id: number, app_user_id: string, agent_number?: string }} payload
 *   agent_number — optional PSTN mobile fallback (e.g. "919876543210")
 */
export async function provisionAppAgent(appRowId, payload) {
  const { data } = await api.post(`/telephony/apps/${appRowId}/agents`, payload)
  return data
}

/**
 * Update an agent's SIP device status (online / offline) for this app.
 * Exotel's API: PUT /v2/integrations/device  {UserId, DeviceName, DeviceStatus}
 * @param {number} appRowId
 * @param {number} userId
 * @param {boolean} isOnline
 */
export async function updateAgentDeviceForApp(appRowId, userId, isOnline) {
  const { data } = await api.put(`/telephony/apps/${appRowId}/agents/${userId}/device`, {
    is_online: isOnline,
  })
  return data
}

/** Deprovision an employee from this app. */
export async function deprovisionAppAgent(appRowId, userId) {
  const { data } = await api.delete(`/telephony/apps/${appRowId}/agents/${userId}`)
  return data
}

// ── Softphone / logs / dialer (unchanged) ────────────────────────────────

export async function fetchAgentSession(forceRefresh = false) {
  const params = forceRefresh ? { force_refresh: true } : {}
  const { data } = await api.get('/telephony/agent-session', { params })
  return data
}

export async function updateAgentDeviceStatus(isOnline) {
  const { data } = await api.post('/telephony/agent-device', { is_online: isOnline })
  return data
}

export async function fetchContactsList(search = '') {
  const params = search ? { search } : {}
  const { data } = await api.get('/telephony/contacts', { params })
  return data
}

export async function fetchCallLogs({
  page = 1,
  pageSize = 20,
  limit,
  search,
  searchField,
  direction,
  status,
  projectId,
  agentUserId,
  fromDate,
  toDate,
  myCallsOnly = false,
} = {}) {
  const resolvedSize = limit || pageSize
  const params = {
    page,
    page_size: resolvedSize,
    my_calls_only: myCallsOnly,
  }
  if (search && search.trim()) params.search = search.trim()
  if (searchField && searchField !== 'All') params.search_field = searchField
  if (direction && direction !== 'ALL' && direction !== 'All') params.direction = direction
  if (status && status !== 'ALL' && status !== 'All') params.status = status
  if (projectId && projectId !== 'ALL' && projectId !== 'All') params.project_id = projectId
  if (agentUserId && agentUserId !== 'ALL' && agentUserId !== 'All') params.agent_user_id = agentUserId
  if (fromDate) params.from_date = fromDate
  if (toDate) params.to_date = toDate

  const { data } = await api.get('/telephony/call-logs', { params })
  return data
}

/** Fetch or enrich call recording URL on-demand. */
export async function fetchCallRecording(callSid) {
  const { data } = await api.get(`/telephony/calls/${callSid}/recording`)
  return data
}

/** Synchronize an individual call log directly from Exotel's Calls REST API. */
export async function syncCallFromExotel(callSid) {
  const { data } = await api.post(`/telephony/calls/${callSid}/sync`)
  return data
}

/** Gap 1: Edit call_notes and/or project_id (24h immutability enforced server-side). */
export async function patchCallLog(callLogId, { call_notes, project_id } = {}) {
  const { data } = await api.patch(`/telephony/call-logs/${callLogId}`, {
    call_notes,
    project_id,
  })
  return data
}

/** Gap 3: Get real-time agent presence from Universal Cache / Exotel Bulk API. */
export async function fetchQueueStatus(forceRefresh = false) {
  const params = forceRefresh ? { refresh: true } : {}
  const { data } = await api.get('/telephony/queue-status', { params })
  return data
}

/** Gap 4: Get rich caller context (open tickets, open tasks, last interaction). */
export async function fetchCallerContext(phone) {
  const { data } = await api.get('/telephony/caller-context', { params: { phone } })
  return data
}

/** Gap 4: Quick-create a task from active call drawer. */
export async function quickCreateTask(payload) {
  const { data } = await api.post('/telephony/quick-create-task', payload)
  return data
}

/** Gap 4: Quick-create a job under an existing task from active call drawer. */
export async function quickCreateJob(payload) {
  const { data } = await api.post('/telephony/quick-create-job', payload)
  return data
}

// ── Callbacks (Gap 2) ────────────────────────────────────────────────────────

/** Fetch callbacks for current agent (all if manager/admin). status = 'PENDING'|'COMPLETED'|'CANCELLED'|'ALL' */
export async function fetchCallbacks(status = 'PENDING') {
  const { data } = await api.get('/telephony/callbacks', { params: { status } })
  return data
}

/** Schedule a new callback reminder. */
export async function createCallback(payload) {
  const { data } = await api.post('/telephony/callbacks', payload)
  return data
}

/**
 * Update a callback.
 * @param {number} callbackId
 * @param {'complete'|'reschedule'|'cancel'} action
 * @param {object} extra - extra payload (e.g. scheduled_at for reschedule)
 */
export async function patchCallback(callbackId, action, extra = {}) {
  const { data } = await api.patch(`/telephony/callbacks/${callbackId}`, {
    action,
    ...extra,
  })
  return data
}

/** Gap 5: Agent confirms post-call timesheet, optionally updating notes. */
export async function confirmCallTimesheet(timesheetId, callNotes) {
  const { data } = await api.patch(`/telephony/call-timesheet/${timesheetId}`, {
    call_notes: callNotes,
  })
  return data
}

/** Record client-side call disposition (e.g. Agent Declined 'busy'). */
export async function updateCallDisposition(callSid, status = 'busy', reason = 'agent_declined') {
  const { data } = await api.post(`/telephony/calls/${callSid}/disposition`, {
    status,
    reason,
  })
  return data
}

/** Fetch all provisioned telephony agents for filtering and assignment. */
export async function fetchProvisionedAgents() {
  const { data } = await api.get('/telephony/agents')
  return data?.data || []
}

/**
 * Constructs an authenticated streaming proxy URL for a call recording.
 * Passes authToken as a query parameter so HTML5 <audio> tags
 * stream directly from our FastAPI backend without browser Basic Auth sign-in modals.
 * @param {string} callSid
 */
export function getRecordingStreamUrl(callSid) {
  if (!callSid) return ''
  const token = localStorage.getItem('authToken') || ''
  const apiBase = String(import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000').trim().replace(/\/+$/, '')
  const url = `${apiBase}/telephony/calls/${encodeURIComponent(callSid)}/recording-stream`
  return token ? `${url}?token=${encodeURIComponent(token)}` : url
}



